package com.evoapps.evoshift.domain.usecase.user

import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope

/**
 * SH-235: the names of the people on a workplace's shifts, the one resolver every screen showing
 * who works a shift uses (daily records, roster builder, schedule, dashboards, clock review).
 *
 * Shifts carry only the person's id. The names come from the workplace's team (one query: everyone
 * actively engaged there, whatever their other workplaces, primary engagement or owner), then
 * from the profile of each person still missing, read one by one: someone whose engagement here
 * ended (still on past shifts), a profile the team query left out. firestore.rules let the
 * workplace's people read the profile of anyone engaged there in any state (colleagueAt).
 *
 * The result holds the team's names and the ones read. People found nowhere (an account deleted,
 * a profile without a name) are left out and logged: callers show [PersonNames.UNNAMED] and the
 * screens a label ("Employee"), never a blank or an id.
 */
class ResolvePersonNamesUseCase(private val userRepository: UserRepository) {

    /**
     * Names by user id for [userIds] at [workplaceId]; [team] when the caller already read it (no
     * second team query), else it is read here. A failed team read still names the people from
     * their profiles.
     */
    suspend operator fun invoke(
        workplaceId: String,
        userIds: Collection<String>,
        team: Collection<User>? = null
    ): Map<String, String> = coroutineScope {
        val members = team ?: userRepository.getAssignableEmployees(workplaceId).getOrNull().orEmpty()
        val known = members.mapNotNull { user -> user.displayName.takeIf { it.isNotBlank() }?.let { user.uid to it } }.toMap()
        val missing = userIds.filter { it.isNotBlank() && it !in known }.toSet()
        val read = missing.map { uid ->
            async { uid to userRepository.getUserProfile(uid).getOrNull()?.displayName?.takeIf { it.isNotBlank() } }
        }.awaitAll()
        val names = known + read.mapNotNull { (uid, name) -> name?.let { uid to it } }
        val unnamed = missing.filter { it !in names }
        if (unnamed.isNotEmpty()) println("SH-235: no name for ${unnamed.size} person(s) on shifts of $workplaceId: ${unnamed.sorted()}")
        names
    }

    /**
     * The ids of the people on [shifts]: the assignee, who actually worked it (SH-128) and, on a
     * split part, who works the other part (SH-236/SH-237: the lines and dialogs name them too).
     */
    fun peopleOn(shifts: Collection<Shift>): Set<String> =
        shifts.flatMapTo(LinkedHashSet()) { listOfNotNull(it.userId, it.actualUserId, it.split?.otherUserId) }
}

/** SH-235: naming shifts from a resolved name map. */
object PersonNames {
    /** A person nobody could name: the screens show their label for it ("Employee"). */
    const val UNNAMED = ""

    /** [shifts] with each assignee's name from [names], else the name the shift already had. */
    fun named(shifts: List<Shift>, names: Map<String, String>): List<Shift> = shifts.map { shift ->
        val name = shift.userId?.let(names::get) ?: return@map shift
        if (name == shift.userDisplayName) shift else shift.copy(userDisplayName = name)
    }
}
